import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import type { Item } from "./api.ts";
import {
  heldWords,
  type ItemIconName,
  itemIcon,
  type Kind,
  kindClass,
  kindOf,
  SHORT_FACT,
  titleParts,
} from "./format.ts";
import { isTripmoIcon, TRIPMO_ICONS } from "./icons.ts";
import { Mark } from "./mark.tsx";
import { useReadOnly } from "./readOnly.ts";
import { navigate } from "./router.ts";
import { Button, Icon } from "./souvenir.ts";
import { useViewing } from "./viewing.ts";

export { Mark, WAVE_BACK, WAVE_FRONT } from "./mark.tsx";

// Tripmo's logo: a link to Your trips on every page, a trip's bar included (DESIGN.md, "The trip's name is
// its menu; Back stays inside the trip"). A step inside the app, as the trip menu's Your trips is, so the
// trip writes down where it was before it's left and its card on Your trips puts it back there (reopen.ts);
// a click that asks for a new tab or window is the browser's.
export function Wordmark() {
  return (
    <a
      className="tm-wordmark"
      href="/"
      aria-label="Tripmo, your trips"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        navigate("/");
      }}
    >
      <Mark />
      Tripmo
    </a>
  );
}

// A count of things waiting on you: questions to answer, people asking to join.
export function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="tm-badge" aria-hidden="true">
      {count > 99 ? "99+" : count}
    </span>
  );
}

// How many things are under a heading, only when there are some: never "Reminders 0" (DESIGN.md,
// Views). An empty section says so in its own quiet line instead (Empty).
export function Count({ n }: { n: number }) {
  if (n <= 0) return null;
  return <span className="tm-muted"> {n}</span>;
}

// A section's heading, with its count when something is under it.
export function SectionTitle({
  id,
  count,
  className = "title",
  children,
}: {
  id: string;
  count?: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <h2 id={id} className={className}>
      {children}
      {count !== undefined && <Count n={count} />}
    </h2>
  );
}

// What an empty view or section says, under its heading, in one voice (features/getting-started.md,
// "Just in time, inside the trip"): what will be here and how it gets here (your AI, as plans firm up),
// with a prompt to copy where there's something to ask for. On a read-only trip nothing more gets here, and
// what a prompt asks for would be refused (features/paying.md, "Spots"): it says `readOnly` instead, with
// no prompt, which every prompt has to say, unless the prompt only reads (`promptReads`: it stays).
type EmptyProps = { children: ReactNode } & (
  | { prompt?: undefined; readOnly?: ReactNode; promptReads?: undefined }
  | { prompt: string; readOnly: ReactNode; promptReads?: boolean }
);
export function Empty({ children, prompt, readOnly, promptReads = false }: EmptyProps) {
  const locked = useReadOnly();
  // A shared trip's viewer has no AI of theirs in it: never a prompt, the read-only words where there are some.
  const viewing = useViewing() !== null;
  if (locked && (!promptReads || viewing) && readOnly !== undefined)
    return <p className="callout tm-muted tm-empty">{readOnly}</p>;
  if (prompt === undefined || viewing) return <p className="callout tm-muted tm-empty">{children}</p>;
  return (
    <div className="tm-empty">
      <p className="callout tm-muted">{children}</p>
      <Prompt text={prompt} />
    </div>
  );
}

// Words to say to your AI, as the person would say them, with Copy beside them. Where copying them is
// the one thing to do on the screen (connecting an AI, starting the first trip), `primary` makes Copy its
// main button, under the words; `onCopied` hears each copy that went through. `label` names it for
// screen readers ("What to send your AI").
export function Prompt({
  text,
  primary = false,
  label,
  onCopied,
}: {
  text: string;
  primary?: boolean;
  label?: string;
  onCopied?: () => void;
}) {
  return (
    <div
      className={`tm-prompt${primary ? " is-primary" : ""}`}
      {...(label ? { role: "group", "aria-label": label } : {})}
    >
      <PromptWords text={text} />
      <CopyButton
        text={text}
        {...(primary ? { variant: "primary" as const, size: "md" as const } : {})}
        {...(onCopied ? { onCopied } : {})}
      />
    </div>
  );
}

// The words alone, after what leads into them ("Ask your AI:"). Where the person adds their own, [in
// brackets], it's quieter.
export function PromptWords({ text, lead }: { text: string; lead?: string }) {
  const parts = text.split(/(\[[^\]]*\])/);
  return (
    <p className="tm-prompt-words">
      {lead && <span className="tm-prompt-lead">{lead} </span>}
      <q>
        {parts.map((part, i) =>
          part.startsWith("[") ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence, in order
            <span key={i} className="tm-prompt-yours">
              {part}
            </span>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence, in order
            <Fragment key={i}>{part}</Fragment>
          ),
        )}
      </q>
    </p>
  );
}

// A page that's only a message names the tab after it; a page around it may name it better, after.
export function Message({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Tripmo`;
  }, [title]);
  return (
    <main className="tm-message">
      <Wordmark />
      <h1 className="display-l">{title}</h1>
      <p className="body">{body}</p>
      {action}
    </main>
  );
}

// From a trip that didn't open (or broke while it was drawn), back to Your trips: a step inside the app.
export function BackToTrips() {
  return (
    <a
      className="tm-link body"
      href="/"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate("/");
      }}
    >
      Back to your trips
    </a>
  );
}

// A link to hand on (the MCP URL, a join link), with a Copy button. Too long for its line, it breaks
// after a slash ("https://", "plan-dev.tripmo.me/", "mcp"), not at a hyphen or anywhere else, unless a
// part is longer than the whole line.
export function CopyLink({ url }: { url: string }) {
  const parts = url.split(/(?<=\/)(?!\/)/);
  return (
    <div className="tm-copy">
      <code className="callout">
        {parts.map((part, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one address, in order
          <span key={i} className="tm-copy-part">
            {part}
          </span>
        ))}
      </code>
      <CopyButton text={url} />
    </div>
  );
}

// A link that goes somewhere else (Stripe's Checkout, a server page), looking like Souvenir's Button.
export function ButtonLink({
  href,
  variant = "secondary",
  size = "md",
  className,
  children,
}: {
  href: string;
  variant?: "primary" | "secondary" | "quiet";
  size?: "sm" | "md";
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      className={`sv-btn sv-btn--${variant} sv-btn--${size} tm-button-link${className ? ` ${className}` : ""}`}
      href={href}
    >
      <span>{children}</span>
    </a>
  );
}

// Copies the text, and says so for a moment. `onCopied` hears each copy that went through; one that
// can't (no clipboard here) leaves the text on the page to select by hand.
export function CopyButton({
  text,
  variant = "secondary",
  size = "sm",
  label = "Copy",
  className,
  onCopied,
}: {
  text: string;
  variant?: "primary" | "secondary" | "quiet";
  size?: "sm" | "md";
  label?: string;
  className?: string;
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(timer.current ?? undefined), []);
  return (
    <Button
      variant={variant}
      size={size}
      {...(className ? { className } : {})}
      {...(copied ? { icon: "check" as const } : {})}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // No clipboard here (a page not served securely): the words are there to select by hand.
          return;
        }
        setCopied(true);
        clearTimeout(timer.current ?? undefined);
        timer.current = setTimeout(() => setCopied(false), 2000);
        onCopied?.();
      }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
}

// A thing's icon on its kind's colour, the same in every list, card and chip as on its pin. An AI's
// suggestion nobody has rated is a paper disc with a dashed ring in the kind's colour; a choice's option,
// its colour ringed with a dashed line, as its pin is; something called off is grey.
export function KindIcon({
  item,
  size = 18,
  suggested = false,
  option = false,
  className = "tm-row-icon",
}: {
  item: Item;
  size?: number;
  suggested?: boolean;
  // One of a choice's options, ringed as on the map.
  option?: boolean;
  className?: string;
}) {
  const state =
    item.status === "cancelled" ? " is-cancelled" : option ? " is-option" : suggested ? " is-suggested" : "";
  return (
    <span className={`${className} ${kindClass(kindOf(item))}${state}`}>
      <ItemIcon name={itemIcon(item)} size={size} />
    </span>
  );
}

// A thing's icon: Souvenir's, or Tripmo's own (icons.ts) drawn exactly as Souvenir draws its icons.
export function ItemIcon({ name, size = 20 }: { name: ItemIconName; size?: number }) {
  if (!isTripmoIcon(name)) return <Icon name={name} size={size} />;
  return (
    <svg
      className="sv-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Tripmo's own constant icon markup, as Souvenir's Icon does
      dangerouslySetInnerHTML={{ __html: TRIPMO_ICONS[name] }}
    />
  );
}

// Souvenir's MapPin, with any of the item icons in its head: the same markup, so Souvenir's pin styles
// (and Tripmo's on top of them) apply unchanged.
export function ItemPin({ icon, selected, label }: { icon: ItemIconName; selected: boolean; label: string }) {
  return (
    <span className={`sv-pin${selected ? " is-selected" : ""}`} role="img" aria-label={label}>
      <span className="sv-pin-head">
        <ItemIcon name={icon} size={18} />
      </span>
      <span className="sv-pin-tail" />
    </span>
  );
}

// A section's heading that folds it (DESIGN.md, Views: every section of Ideas and of Bookings folds by its
// heading, and Money by its own): the heading is one button, its words, its count (so what waits on me still
// shows folded) and a caret at the line's end, pointing down while folded and up once open. The heading keeps its id and its look (`className`), so the section is still named by it.
export function FoldHeading({
  id,
  className = "title",
  count,
  open,
  controls,
  onToggle,
  children,
}: {
  id: string;
  className?: string;
  count?: number;
  open: boolean;
  // The id of what it folds.
  controls: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <h2 id={id} className={`tm-section-fold${open ? " is-open" : ""} ${className}`}>
      <button
        type="button"
        className="tm-section-fold-button"
        aria-expanded={open}
        aria-controls={controls}
        onClick={onToggle}
      >
        <span className="tm-section-fold-words">
          {children}
          {count !== undefined && <Count n={count} />}
        </span>
        <Icon name="chevron" size={16} className="tm-fold-caret tm-section-fold-caret" />
      </button>
    </h2>
  );
}

// A section of Ideas or of Bookings (DESIGN.md, Views: Bookings laid out as Ideas is, the founder,
// 2026-10-23), in one place so the two can't drift apart: between hairlines, under its heading in the eyebrow
// style with its count, folding to that one line; open, a caption under the heading says what it holds, and
// what it holds is under that (`id`-body), laid out by `bodyClassName`.
export function PageSection({
  id,
  title,
  count,
  caption,
  open,
  onToggle,
  bodyClassName = "tm-section-body",
  children,
}: {
  id: string;
  title: string;
  count?: number;
  caption: string;
  open: boolean;
  onToggle: () => void;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <section className="tm-page-section" aria-labelledby={id}>
      <header className="tm-group-head">
        <FoldHeading
          id={id}
          className="eyebrow tm-group-title"
          {...(count !== undefined ? { count } : {})}
          open={open}
          controls={`${id}-body`}
          onToggle={onToggle}
        >
          {title}
        </FoldHeading>
        {open && <p className="caption tm-muted tm-group-caption">{caption}</p>}
      </header>
      {open && (
        <div id={`${id}-body`} className={bodyClassName}>
          {children}
        </div>
      )}
    </section>
  );
}

// One kind of thing, folding like a choice (Ideas' Suggestions, Bookings' kinds; DESIGN.md, Views): its head
// is one button, its icon on its kind's colour, its name and how many in the title style, a chip saying what's
// left in it (on a phone under the name), and a caret; it folds and unfolds what's under it (`${id}-list`).
// Everything under the head steps in to start under its name, the kind's icon alone at the edge, so each row
// visibly hangs under its kind (DESIGN.md, Bookings, "A kind's rows step in"; the founder, 2026-10-23): one
// wrapper here, for Bookings and Ideas alike.
export function KindFold({
  id,
  kind,
  icon,
  label,
  count,
  chip,
  folded,
  onFold,
  children,
}: {
  id: string;
  kind: Kind;
  icon: ItemIconName;
  label: string;
  count: number;
  chip: ReactNode;
  folded: boolean;
  onFold: (folded: boolean) => void;
  children: ReactNode;
}) {
  return (
    <section className={`tm-choice tm-kind-fold${folded ? " is-folded" : ""}`} aria-labelledby={id}>
      <h3 className="tm-choice-heading">
        <button
          type="button"
          className="tm-choice-head"
          aria-expanded={!folded}
          aria-controls={`${id}-list`}
          onClick={() => onFold(!folded)}
        >
          <span className={`tm-row-icon ${kindClass(kind)}`}>
            <ItemIcon name={icon} size={18} />
          </span>
          <span className="tm-choice-title">
            <span id={id} className="title">
              {label}
              <Count n={count} />
            </span>
          </span>
          <span className="tm-choice-state">{chip}</span>
          <Icon name="chevron" size={18} className="tm-choice-caret" />
        </button>
      </h3>
      {!folded && (
        <div id={`${id}-list`} className="tm-kind-body">
          {children}
        </div>
      )}
    </section>
  );
}

// Waiting for you, first on Ideas and on Bookings while anything there waits on me (DESIGN.md, Views, "What
// waits on me leads"): raised like a card, never folding; its heading counts exactly what the view's tab does,
// a line says what that is, then what it holds (`children`), and on Ideas Start rating beside it.
export function WaitingCard({
  count,
  line,
  action,
  children,
}: {
  count: number;
  line: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="tm-waiting" aria-labelledby="waiting-title">
      <div className="tm-waiting-text">
        <h2 id="waiting-title" className="title tm-waiting-title">
          Waiting for you
          <Count n={count} />
        </h2>
        <p className="callout tm-muted tm-waiting-line">{line}</p>
        {children}
      </div>
      {action}
    </section>
  );
}

// A line that folds what's under it ("6 more, from Thu 19 Nov", "7 decided"): the words, and a caret
// that points down while it's folded and up once open.
export function FoldLine({
  open,
  controls,
  onToggle,
  children,
}: {
  open: boolean;
  // The id of what it opens.
  controls: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`tm-fold-line${open ? " is-open" : ""}`}
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
    >
      <span className="callout">{children}</span>
      <Icon name="chevron" size={16} className="tm-fold-caret" />
    </button>
  );
}

// A line of facts, " · " between them ("13.0 km from Hotel Granvia Kyoto · Suggested by Claude"). Where the
// line wraps, it wraps between facts: a short one never breaks inside (a booking's reference, "in 5 days"),
// a long one between its words, never inside one, a date or an amount (`Held`), and the " ·" holds on to the
// fact before it, so it never starts a line (seen 2026-10-20 in the deck: "· Suggested by Cursor" on a line of
// its own). Its text is the facts joined by " · ", as said. A fact that isn't words (how far a due date is, a
// flag) keeps its own look.
export function factLine(parts: readonly (ReactNode | null | undefined | false)[]): ReactNode {
  const shown = parts.filter(
    (p): p is NonNullable<ReactNode> => p !== null && p !== undefined && p !== false && p !== "",
  );
  return shown.map((fact, k) => {
    const more = k < shown.length - 1;
    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: one line's facts, in order
      <Fragment key={k}>
        {typeof fact === "string" && fact.length <= SHORT_FACT ? (
          <span className="tm-fact">
            {fact}
            {more && " ·"}
          </span>
        ) : (
          <>
            {typeof fact === "string" ? <Held text={fact} /> : fact}
            {more && <FactGap />}
          </>
        )}
        {more && " "}
      </Fragment>
    );
  });
}

// The " ·" after a fact, held to it: the line breaks only after it.
export function FactGap() {
  return <span className="tm-fact"> ·</span>;
}

// Words that break only between their units (`heldWords`; DESIGN.md, Bookings: "a line breaking between facts
// or words and never inside a booking reference, an amount or a date", the lead's call, 2026-10-23, after the
// founder's Nagano hotel broke "3906-5167-1399 / 3906-" from "5168-9376" at 1542 px). A unit longer than a short
// fact wraps as it must, so nothing runs off a phone's edge.
export function Held({ text }: { text: string }) {
  return heldWords(text).map((unit, k) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: one text's words, in order
    <Fragment key={k}>
      {k > 0 && " "}
      {unit.length <= SHORT_FACT ? <span className="tm-held">{unit}</span> : unit}
    </Fragment>
  ));
}

// A row's title, wrapping between its words and never inside a date in it ("for 25" over "Dec"; `titleParts`):
// a booking's row and a to-do's, wherever it shows (DESIGN.md, Bookings: "a row's title shows in full").
export function Title({ text }: { text: string }) {
  return titleParts(text).map((part, k) =>
    part.date ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: one title's parts, in order
      <span key={k} className="tm-held">
        {part.text}
      </span>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: one title's parts, in order
      <Fragment key={k}>{part.text}</Fragment>
    ),
  );
}
